import { useCallback, useSyncExternalStore } from 'react';
import { useRoomContext } from '@livekit/components-react';
import type { Participant, Room } from 'livekit-client';

export type ReactionLogEntry = {
    id: string;
    from: { identity: string; name: string; isLocal: boolean };
    emojis: string;
    timestamp: number;
};

type Burst = { entryId: string; lastAt: number; count: number };

type ReactionLog = {
    entries: ReactionLogEntry[];
    bursts: Map<string, Burst>;
    listeners: Set<() => void>;
    nextId: number;
};

const BURST_GAP_MS = 500;
const MAX_EMOJIS_PER_ENTRY = 10;
const MAX_ENTRIES = 500;

const logs = new WeakMap<Room, ReactionLog>();

function logFor(room: Room): ReactionLog {
    let log = logs.get(room);
    if (!log) {
        log = { entries: [], bursts: new Map(), listeners: new Set(), nextId: 0 };
        logs.set(room, log);
    }
    return log;
}

export function recordReaction(room: Room, emoji: string, participant: Participant) {
    const log = logFor(room);
    const now = Date.now();
    const burst = log.bursts.get(participant.identity);
    const burstEntry =
        burst && now - burst.lastAt < BURST_GAP_MS && burst.count < MAX_EMOJIS_PER_ENTRY
            ? log.entries.findLast((entry) => entry.id === burst.entryId)
            : undefined;

    if (burst && burstEntry) {
        const grown = { ...burstEntry, emojis: burstEntry.emojis + emoji };
        log.entries = log.entries.map((entry) => (entry === burstEntry ? grown : entry));
        log.bursts.set(participant.identity, { ...burst, lastAt: now, count: burst.count + 1 });
    } else {
        const entryId = `${participant.identity}:${String(log.nextId++)}`;
        const entry = {
            id: entryId,
            from: { identity: participant.identity, name: participant.name ?? '', isLocal: participant.isLocal },
            emojis: emoji,
            timestamp: now,
        };
        log.entries = [...log.entries.slice(-(MAX_ENTRIES - 1)), entry];
        log.bursts.set(participant.identity, { entryId, lastAt: now, count: 1 });
    }
    for (const listener of log.listeners) listener();
}

export function useReactionLog(): ReactionLogEntry[] {
    const log = logFor(useRoomContext());
    const subscribe = useCallback(
        (listener: () => void) => {
            log.listeners.add(listener);
            return () => {
                log.listeners.delete(listener);
            };
        },
        [log]
    );
    return useSyncExternalStore(subscribe, () => log.entries);
}
